/**
 * Invisible Man — „Cobble & Fog".
 *
 * Postava stojí na troch žetónoch hmly. V hmle sa bráni lepšie (+1 k obrannej
 * karte), medzi hmlami skáče, akoby susedili, a polovica kariet hmlu presúva —
 * často aj súperovi do rúk („your opponent moves a fog token").
 *
 * Z poznámok k balíčku: hmla sa na začiatku kladie ako sidekick (nie na
 * figúrku), potom ju hýbu len jeho karty a smie prejsť aj skončiť kdekoľvek,
 * aj na figúrke či inej hmle.
 */

import { freeSpacesInZonesOf } from '../movement.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  dealDamage,
  drawCard,
  gainAction,
  getMap,
  healFighter,
  heroOf,
  removeFromBoard,
  tokensOf,
} from '../ops.js';
import type { PlayerIndex } from '../types.js';
import { adjacentFighters, askMove, enemyFighters, fighterOrNull, hasFog, opponentOf } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const FOG_TOKENS = 3;

function fogIds(ctx: Ctx): string[] {
  return tokensOf(ctx.state)
    .filter((t) => t.kind === 'fog')
    .map((t) => t.id);
}

/** Stojí Invisible Man (hrdina hráča) na hmle? */
function heroInFog(ctx: Ctx, player: PlayerIndex): boolean {
  const hero = heroOf(ctx.state, player);
  return Boolean(hero && hasFog(ctx, hero.spaceId));
}

/** „Your opponent moves a fog token up to N spaces." */
function opponentMovesFog(e: EffectCtx, max: number, exceptMoved = false) {
  const opponent = opponentOf(e);
  if (opponent === null) return null;
  return {
    kind: 'moveToken' as const,
    player: opponent,
    max,
    exceptMoved,
    optional: true,
    label: `Efekt súpera: posuň ${exceptMoved ? 'inú ' : ''}hmlu Invisible Mana až o ${max} priestory`,
  };
}

/**
 * „Draw 1 card. Move 1 fog token up to 2 spaces, then your opponent moves
 * a different fog token up to 2 spaces."
 */
const covertPreparation: CardEffect = {
  after: (e) => {
    drawCard(e.ctx, e.player);
    const follow = opponentMovesFog(e, 2, true);
    askChoice(e.ctx, {
      kind: 'token',
      player: e.player,
      source: e.cardId,
      label: 'Posuň hmlu až o 2 priestory',
      optional: true,
      tokens: fogIds(e.ctx),
      max: 2,
      empty: false,
      ...(follow ? { follow, onSkip: { ...follow, exceptMoved: false } } : {}),
    });
  },
};

/**
 * „If Invisible Man is on a space with a fog token, all opposing fighters on
 * spaces with fog tokens take 1 damage."
 */
const dreamingOfRevenge: CardEffect = {
  after: (e) => {
    if (!heroInFog(e.ctx, e.player)) return;
    for (const id of enemyFighters(e.ctx, e.player)) {
      const fighter = fighterOrNull(e.ctx, id);
      if (fighter && hasFog(e.ctx, fighter.spaceId)) dealDamage(e.ctx, id, 1);
    }
  },
};

/**
 * „Your opponent may choose to discard 1 card. If they do not, you may move
 * each fog token to any other space." Súper vyberá z vlastnej ruky; keď
 * neodhodí (alebo nemá čo), nasleduje presun hmly.
 */
const confound: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null) return;
    const followUp = { kind: 'moveEveryToken' as const, player: e.player };
    const hand = e.ctx.state.players[opponent]?.hand ?? [];
    if (hand.length === 0) {
      e.ctx.state.pending.push({
        kind: 'option',
        player: e.player,
        source: e.cardId,
        label: 'Súper nemá čo odhodiť — presunieš hmlu',
        optional: false,
        options: [{ label: 'Presúvať hmlu', follow: followUp }],
      });
      return;
    }
    askChoice(e.ctx, {
      kind: 'card',
      player: opponent,
      source: e.cardId,
      label: 'Confound: odhoď 1 kartu, inak súper smie presunúť všetku hmlu',
      optional: true,
      from: 'hand',
      purpose: 'discard',
      onSkip: followUp,
    });
  },
};

/**
 * „IMMEDIATELY: The value of your opponent's attack or defense is 0 and cannot
 * be changed by card effects."
 */
const impossibleToSee: CardEffect = {
  immediate: (e) => {
    e.ctx.state.combat?.flags?.push(e.role === 'attacker' ? 'zeroDefense' : 'zeroAttack');
  },
};

/**
 * „IMMEDIATELY: Cancel all effects on your opponent's card. AFTER COMBAT: If
 * Invisible Man is on a space with a fog token, move that fog token to
 * another space."
 */
const surpriseAttack: CardEffect = {
  cancelsOpponent: true,
  after: (e) => {
    const hero = heroOf(e.ctx.state, e.player);
    if (!hero) return;
    const here = tokensOf(e.ctx.state).filter((t) => t.kind === 'fog' && t.spaceId === hero.spaceId);
    askChoice(e.ctx, {
      kind: 'token',
      player: e.player,
      source: e.cardId,
      label: 'Surprise Attack: presuň hmlu spod Invisible Mana na iný priestor',
      optional: false,
      tokens: here.map((t) => t.id),
      max: null,
      empty: false,
    });
  },
};

/** „Move 1 fog token to a space without a fighter, then place Invisible Man on that space." */
const slipAway: CardEffect = {
  after: (e) => {
    if (!heroOf(e.ctx.state, e.player)) return;
    askChoice(e.ctx, {
      kind: 'token',
      player: e.player,
      source: e.cardId,
      label: 'Slip Away: presuň hmlu na priestor bez figúrky — Invisible Man sa tam presunie',
      optional: false,
      tokens: fogIds(e.ctx),
      max: null,
      empty: true,
      follow: { kind: 'placeHeroOnToken' },
    });
  },
};

/** „If Invisible Man started this turn on a space with a fog token, this card's value is 5 instead." */
const emergeFromMist: CardEffect = {
  during: (e, value) => {
    const hero = heroOf(e.ctx.state, e.player);
    if (!hero) return value;
    const start = e.ctx.state.turn.startSpaces[hero.id];
    const fogAtStart = Object.values(e.ctx.state.turn.startTokens ?? {});
    return start && fogAtStart.includes(start) ? 5 : value;
  },
};

/**
 * „Draw 1 card and choose 1 effect: move Invisible Man to a space with a fog
 * token; move 1 fog token up to 3 spaces."
 */
const lurking: CardEffect = {
  after: (e) => {
    drawCard(e.ctx, e.player);
    e.ctx.state.pending.push({
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Lurking: vyber jeden efekt',
      optional: false,
      options: [
        { label: 'Presuň Invisible Mana na hmlu', follow: { kind: 'placeHeroOnFog' } },
        {
          label: 'Posuň hmlu až o 3',
          follow: {
            kind: 'moveToken',
            player: e.player,
            max: 3,
            optional: true,
            label: 'Posuň hmlu až o 3 priestory',
          },
        },
      ],
    });
  },
};

/** „Move Invisible Man up to 1 space. Your opponent then moves a fog token up to 3 spaces." */
const intoThinAir: CardEffect = {
  after: (e) => {
    const hero = heroOf(e.ctx.state, e.player);
    if (hero) askMove(e, { fighters: [hero.id], max: 1, label: 'Presuň Invisible Mana až o 1 priestor' });
    const follow = opponentMovesFog(e, 3);
    if (!follow) return;
    askChoice(e.ctx, {
      kind: 'token',
      player: follow.player,
      source: e.cardId,
      label: follow.label,
      optional: true,
      tokens: fogIds(e.ctx),
      max: 3,
      empty: false,
    });
  },
};

/**
 * „Draw 3 cards, then choose 2 cards from your hand and put them on top of
 * your deck in any order." Druhá vybraná karta skončí navrchu.
 */
const codedNotes: CardEffect = {
  after: (e) => {
    for (let i = 0; i < 3; i++) drawCard(e.ctx, e.player);
    for (const label of [
      'Coded Notes: polož kartu navrch balíčka (1/2)',
      'Coded Notes: polož kartu navrch balíčka (2/2 — táto bude úplne navrchu)',
    ]) {
      askChoice(e.ctx, {
        kind: 'card',
        player: e.player,
        source: e.cardId,
        label,
        optional: false,
        from: 'hand',
        purpose: 'topOfDeck',
      });
    }
  },
};

/** „If Invisible Man is on a space with a fog token, deal 2 damage to any one opposing fighter." */
const reignOfTerror: CardEffect = {
  scheme: (e) => {
    if (!heroInFog(e.ctx, e.player)) return;
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'Reign of Terror: 2 poškodenia ľubovoľnej súperovej figúrke',
      optional: false,
      amount: 2,
      targets: enemyFighters(e.ctx, e.player),
    });
  },
};

/**
 * „Recover 1 health. Remove Invisible Man from the board. At the start of your
 * next turn, place Invisible Man in any space. (If you played this as your
 * first action, end your turn.)" Návrat rieši `applyEndTurn`.
 */
const vanish: CardEffect = {
  scheme: (e) => {
    const hero = heroOf(e.ctx.state, e.player);
    if (!hero) return;
    healFighter(e.ctx, hero.id, 1);
    removeFromBoard(e.ctx, hero.id);
    const turn = e.ctx.state.turn;
    if (turn.player === e.player && turn.actionsUsed.length === 1) {
      // Prvá akcia → ťah končí: zvyšné akcie prepadnú.
      turn.extraActions = Math.min(turn.extraActions ?? 0, 1 - 2);
    }
  },
};

/**
 * „Deal 1 damage to one adjacent fighter. If Invisible Man is on a space with
 * a fog token, deal 3 damage instead. Your opponent then moves a fog token up
 * to 2 spaces."
 */
const stepLightly: CardEffect = {
  scheme: (e) => {
    const hero = heroOf(e.ctx.state, e.player);
    if (hero) {
      const amount = heroInFog(e.ctx, e.player) ? 3 : 1;
      askChoice(e.ctx, {
        kind: 'damage',
        player: e.player,
        source: e.cardId,
        label: `Step Lightly: ${amount} poškodenia susednej figúrke`,
        optional: false,
        amount,
        targets: adjacentFighters(e.ctx, hero.spaceId),
      });
    }
    const follow = opponentMovesFog(e, 2);
    if (!follow) return;
    askChoice(e.ctx, {
      kind: 'token',
      player: follow.player,
      source: e.cardId,
      label: follow.label,
      optional: true,
      tokens: fogIds(e.ctx),
      max: 2,
      empty: false,
    });
  },
};

/** „Move 1 fog token to another space. Gain 1 action." */
const rollingFog: CardEffect = {
  scheme: (e) => {
    askChoice(e.ctx, {
      kind: 'token',
      player: e.player,
      source: e.cardId,
      label: 'Rolling Fog: presuň hmlu na iný priestor',
      optional: false,
      tokens: fogIds(e.ctx),
      max: null,
      empty: false,
    });
    gainAction(e.ctx, e.player);
  },
};

export const invisibleManCards: EffectRegistry = {
  'invisible-man.covert-preparation': covertPreparation,
  'invisible-man.dreaming-of-revenge': dreamingOfRevenge,
  'invisible-man.confound': confound,
  'invisible-man.impossible-to-see': impossibleToSee,
  'invisible-man.surprise-attack': surpriseAttack,
  'invisible-man.slip-away': slipAway,
  'invisible-man.emerge-from-mist': emergeFromMist,
  'invisible-man.lurking': lurking,
  'invisible-man.into-thin-air': intoThinAir,
  'invisible-man.coded-notes': codedNotes,
  'invisible-man.reign-of-terror': reignOfTerror,
  'invisible-man.vanish': vanish,
  'invisible-man.step-lightly': stepLightly,
  'invisible-man.rolling-fog': rollingFog,
};

/**
 * „At the start of the game, after you place Invisible Man, place 3 fog tokens
 * in separate spaces in his zone. When Invisible Man is on a space with a fog
 * token, add 1 to the value of his defense cards. Invisible Man may move
 * between two spaces with fog tokens as if they were adjacent." Skok cez
 * hmlu rieši `movement.ts` (FOG_WALKERS).
 */
export const invisibleManSpecial: HeroSpecial = {
  name: 'Hmla',
  setup: (ctx, player) => {
    const hero = heroOf(ctx.state, player);
    if (!hero) return;
    const occupied = new Set(ctx.state.fighters.map((f) => f.spaceId));
    const spaces = freeSpacesInZonesOf(getMap(ctx), occupied, hero.spaceId);
    for (let i = 1; i <= FOG_TOKENS; i++) {
      askChoice(ctx, {
        kind: 'placeToken',
        player,
        source: null,
        label: `Polož hmlu ${i}/${FOG_TOKENS} na voľný priestor v zóne Invisible Mana`,
        optional: false,
        spaces,
      });
    }
  },
  defenseModifier: (ctx, player, fighterId) => {
    const fighter = fighterOrNull(ctx, fighterId);
    return fighter?.kind === 'hero' && heroInFog(ctx, player) ? 1 : 0;
  },
};
